import { ParsedUrlQuery } from 'querystring'
import { useCallback, useMemo } from 'react'

import { isInteger } from '@unreserved-frontend-v2/utils/numbers'
import { replaceQueryParam } from '@unreserved-frontend-v2/utils/url'

export type PaginationUrlsReturn = {
  pageNumber: number
  prevPageUrl: string
  nextPageUrl: string
}

export function usePaginationUrls(url: string, query: ParsedUrlQuery, firstPage: number) {
  const page = isInteger(query['page'] as string)
    ? Math.max(
        // Subtract 1 to account for commonly readable non-array indexing used for display purposes
        parseInt(query['page'] as string) - 1,
        firstPage
      )
    : firstPage

  /**
   * Method that parses & formats the url for a specific page number
   */
  const getUrlForPage = useCallback(
    (pageNumber: number) => {
      // Duplicating query to ensure values don't get removed for subsequent calls
      const paramsQuery = { ...query }

      // Ensure that the `page` key exists if not paginating to  first page
      if (pageNumber !== firstPage) {
        paramsQuery['page'] = undefined
      } else {
        // Delete the `page` paramsQuery if it is the first page, in which case default takes over
        delete paramsQuery['page']
      }

      // Always delete route based paramsQuery.id
      delete paramsQuery['id']
      delete paramsQuery['listing']

      const params = replaceQueryParam(
        'page',
        // Add 1 to use commonly readable non-array indexing for display purposes
        pageNumber + 1,
        paramsQuery
      )

      return params ? `${url}?${params}` : url
    },
    [firstPage, query, url]
  )

  return useMemo<PaginationUrlsReturn>(() => {
    return {
      pageNumber: page,
      prevPageUrl: page > firstPage ? getUrlForPage(page - 1) : '/',
      nextPageUrl: getUrlForPage(page + 1),
    }
  }, [firstPage, getUrlForPage, page])
}

export default usePaginationUrls
